import { faArrowUpRightFromSquare, faFileLines, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { format } from 'date-fns';
import * as React from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import Alert from '../../../core/components/Alert.tsx';
import Button from '../../../core/components/Button.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import Loader from '../../../core/components/Loader.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import { url } from '../../../core/utils/link.ts';
import Chip, { ChipVariant } from '../../assistants/components/config/Chip.tsx';
import { ASSISTANCE_PATH as ORDER_ASSISTANCE_PATH } from '../../orders/constants.ts';
import { ASSISTANCE_PATH as RFQ_ASSISTANCE_PATH } from '../../rfqs/constants.ts';
import {
    callDuration,
    callerLabel,
    END_REASON_VARIANTS,
    OUTCOME_VARIANTS,
    SENTIMENT_VARIANTS,
} from '../callRecords.ts';

const CloseIcon = withIcon(faXmark);
const FileIcon = withIcon(faFileLines);
const ExternalLinkIcon = withIcon(faArrowUpRightFromSquare);

const DOCUMENT_PATHS: Record<string, string> = {
    ORDER: ORDER_ASSISTANCE_PATH,
    REQUEST_FOR_QUOTATION: RFQ_ASSISTANCE_PATH,
};

const DELIVERY_STATUS_VARIANTS: Record<string, ChipVariant> = {
    sent: 'success',
    failed: 'danger',
    pending: 'muted',
};

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <h3 className="text-xs font-medium text-content-medium">{children}</h3>
);

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <section className="flex flex-col gap-2">
        <SectionTitle>{title}</SectionTitle>
        {children}
    </section>
);

const MetaItem = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-content-medium">{label}</span>
        <span className="text-sm text-content-strong">{children}</span>
    </div>
);

const CallMeta = ({ record }) => {
    const { t } = useTranslation('voice_agents');
    const outcome = record.callOutcome;
    const sentiment = record.sentiment;
    const endReason = record.endReason;

    return (
        <div className="grid grid-cols-2 gap-4">
            <MetaItem label={t('history.table.outcome')}>
                {outcome ? (
                    <Chip variant={OUTCOME_VARIANTS[outcome] || 'default'}>
                        {t(`history.outcome.${outcome}`, { defaultValue: outcome }) as string}
                    </Chip>
                ) : (
                    '—'
                )}
            </MetaItem>
            <MetaItem label={t('history.table.sentiment')}>
                {sentiment ? (
                    <Chip variant={SENTIMENT_VARIANTS[sentiment] || 'default'}>
                        {t(`history.sentiment.${sentiment}`, { defaultValue: sentiment }) as string}
                    </Chip>
                ) : (
                    '—'
                )}
            </MetaItem>
            <MetaItem label={t('history.table.endReason')}>
                {endReason ? (
                    <Chip variant={END_REASON_VARIANTS[endReason] || 'default'}>
                        {t(`history.endReason.${endReason}`, { defaultValue: endReason }) as string}
                    </Chip>
                ) : (
                    '—'
                )}
            </MetaItem>
            <MetaItem label={t('history.table.duration')}>{callDuration(record)}</MetaItem>
            <MetaItem label={t('history.table.createdAt')}>{format(new Date(record.createdAt), 'PPpp')}</MetaItem>
            {record.calledNumber && <MetaItem label={t('history.detail.calledNumber')}>{record.calledNumber}</MetaItem>}
        </div>
    );
};

const CreatedDocuments = ({ documents }) => {
    const { t } = useTranslation('voice_agents');
    if (!documents.length) return null;

    return (
        <Section title={t('history.createdRecords')}>
            <div className="flex flex-col gap-1.5">
                {documents.map((document) => {
                    const path = DOCUMENT_PATHS[document.documentType];
                    const label = t(`history.documentType.${document.documentType}`, {
                        defaultValue: document.documentType,
                    }) as string;
                    const content = (
                        <>
                            <FileIcon className="text-content-medium" />
                            <span className="truncate text-content-strong">
                                {label}
                                {document.subject && <span className="text-content-medium"> · {document.subject}</span>}
                            </span>
                        </>
                    );
                    const boxClass =
                        'flex items-center gap-2 rounded border border-solid border-line-weak bg-surface-weak px-3 py-2 text-sm';

                    if (!path) {
                        return (
                            <div key={document.id} className={boxClass}>
                                {content}
                            </div>
                        );
                    }
                    return (
                        <Link
                            key={document.id}
                            to={url(path, { recordId: document.id })}
                            className={classnames(
                                boxClass,
                                'group transition-colors hover:border-line-brand-default hover:bg-surface-brand-medium-default'
                            )}
                        >
                            {content}
                            <ExternalLinkIcon className="ml-auto text-content-weak transition-colors group-hover:text-content-brand-default" />
                        </Link>
                    );
                })}
            </div>
        </Section>
    );
};

const SummaryDelivery = ({ delivery }) => {
    const { t } = useTranslation('voice_agents');
    const channels = [
        ['email', delivery?.email],
        ['teams', delivery?.teams],
    ].filter(([, channel]) => channel);
    if (!channels.length) return null;

    return (
        <div className="flex flex-col gap-1.5">
            <SectionTitle>{t('history.delivery.title')}</SectionTitle>
            {channels.map(([name, channel]: any) => (
                <div key={name} className="flex flex-col gap-1">
                    <span className="flex flex-wrap items-center gap-1.5 text-sm text-content-strong">
                        {t(`history.delivery.${name}`)}
                        <Chip variant={DELIVERY_STATUS_VARIANTS[channel.status] || 'default'}>
                            {t(`history.delivery.status.${channel.status}`, { defaultValue: channel.status }) as string}
                        </Chip>
                        <span className="text-xs text-content-medium">
                            {(channel.status === 'sent' ? channel.deliveredRecipients : channel.recipients).join(', ')}
                        </span>
                    </span>
                    {channel.status === 'failed' && channel.error && (
                        <span className="text-xs text-content-danger-strong">{channel.error}</span>
                    )}
                </div>
            ))}
        </div>
    );
};

const CallSummary = ({ record }) => {
    const { t } = useTranslation('voice_agents');
    if (!record.summary && !record.summaryDelivery) return null;

    return (
        <Section title={t('history.summary')}>
            {record.summary ? (
                <p className="whitespace-pre-wrap text-sm text-content-strong">{record.summary}</p>
            ) : (
                <span className="text-sm text-content-medium">{t('history.noSummary')}</span>
            )}
            <SummaryDelivery delivery={record.summaryDelivery} />
        </Section>
    );
};

// The agent's messages are gray like the caller's: the `agent` (indigo) token family that DESIGN.md
// reserves for agent-authored content does not exist in tailwind.css yet, and hand-rolled indigo is banned.
const Transcript = ({ record }) => {
    const { t } = useTranslation('voice_agents');
    const entries = record.transcript?.entries || [];

    return (
        <Section title={t('history.transcript')}>
            {record.audioRecordingUrl && (
                <audio controls src={record.audioRecordingUrl} className="w-full">
                    <track kind="captions" />
                </audio>
            )}
            {entries.length > 0 ? (
                <div className="flex flex-col gap-2">
                    {entries.map((entry, index) => (
                        <div
                            key={index}
                            className={classnames(
                                'flex max-w-11/12 flex-col gap-0.5 rounded-lg px-3 py-2 text-sm',
                                entry.role === 'agent'
                                    ? 'self-start bg-surface-strong'
                                    : 'self-end border border-solid border-line-weak bg-surface-weak'
                            )}
                        >
                            <span className="text-xs font-medium text-content-medium">
                                {entry.role === 'agent' ? t('history.roleAgent') : t('history.roleCaller')}
                            </span>
                            <span className="whitespace-pre-wrap text-content-strong">{entry.message}</span>
                        </div>
                    ))}
                </div>
            ) : (
                <span className="text-sm text-content-medium">{t('history.noTranscript')}</span>
            )}
        </Section>
    );
};

interface CallDetailPanelProps {
    record?: any;
    loading?: boolean;
    onClose: () => void;
}

const CallDetailPanel = ({ record, loading, onClose }: CallDetailPanelProps) => {
    const { t } = useTranslation('voice_agents');

    useEffect(() => {
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', closeOnEscape);
        return () => window.removeEventListener('keydown', closeOnEscape);
    }, [onClose]);

    const documents = record?.documents || [];

    return (
        <aside
            aria-label={t('history.detail.title') as string}
            // sits in the layout and pushes the list aside, so it gets a border instead of a shadow (DESIGN.md)
            className="flex h-full w-full max-w-xl shrink-0 flex-col border-l border-solid border-line-weak bg-surface-weak animate-slide-in-from-right"
        >
            <header className="flex h-14 flex-none items-center justify-between gap-3 border-b border-solid border-line-weak px-4">
                <div className="flex min-w-0 flex-col">
                    <span className="truncate text-sm font-medium text-content-strong">
                        {record ? callerLabel(record, t) : t('history.detail.title')}
                    </span>
                    {record?.callerName && record?.callerPhoneNumber && (
                        <span className="truncate text-xs text-content-medium">{record.callerPhoneNumber}</span>
                    )}
                </div>
                <Button variant="ghost" onClick={onClose} aria-label={t('history.detail.close') as string}>
                    <CloseIcon />
                </Button>
            </header>

            <div className="flex flex-1 flex-col gap-6 overflow-auto p-4">
                {!record && loading && <Loader />}
                {!record && !loading && (
                    <span className="text-sm text-content-medium">{t('history.detail.notFound')}</span>
                )}
                {record && (
                    <>
                        {record.errorMessage && (
                            <Alert severity="error">
                                <div className="flex flex-col gap-1">
                                    <span className="font-medium">{t('history.callFailed')}</span>
                                    <span className="whitespace-pre-wrap text-content-strong">
                                        {record.errorMessage}
                                    </span>
                                </div>
                            </Alert>
                        )}
                        <CallMeta record={record} />
                        <CallSummary record={record} />
                        <CreatedDocuments documents={documents} />
                        <Transcript record={record} />
                    </>
                )}
            </div>
        </aside>
    );
};

export default CallDetailPanel;
